import {
  Flex,
  Box,
  Text,
  Button,
  useToast,
  SimpleGrid,
  IconButton,
  Badge,
  Tooltip,
} from '@chakra-ui/react';
import * as React from 'react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import Card from "../../../../components/card/Card";
import { menuService } from '../../../../services/menuService';
import { pricingService } from '../../../../services/pricingService';
import AddCategoryModal from './AddCategoryModal';
import PremiumUpgradeModal from '../../../../components/modals/PremiumUpgradeModal';

// STANDARD plan için maksimum kategori sayısı
const STANDARD_CATEGORY_LIMIT = 10;

export default function CategoryTable({ categories = [], setCategories }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [userPlan, setUserPlan] = React.useState('STANDARD');
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = React.useState(false);
  const toast = useToast();
  
  // Plan bilgisini çek
  React.useEffect(() => {
    const fetchPlan = async () => {
      try {
        const pricingInfo = await pricingService.getPricingInfo();
        if (pricingInfo?.success && pricingInfo?.data?.business?.plan) {
          setUserPlan(pricingInfo.data.business.plan);
        }
      } catch (error) {
        console.error('Plan bilgisi alınamadı:', error);
        const business = JSON.parse(localStorage.getItem('business') || '{}');
        if (business.plan) {
          setUserPlan(business.plan);
        }
      }
    };
    fetchPlan();
  }, []);

  const handleOpenModal = () => {
    // STANDARD plan için kategori limiti kontrolü
    if (userPlan === 'STANDARD' && categories.length >= STANDARD_CATEGORY_LIMIT) {
      toast({
        title: 'Kategori Limiti',
        description: `STANDARD plan için maksimum ${STANDARD_CATEGORY_LIMIT} kategori eklenebilir. Daha fazla kategori eklemek için PREMIUM plana yükseltin.`,
        status: 'warning',
        duration: 5000,
      });
      setIsUpgradeModalOpen(true);
      return;
    }
    setIsOpen(true);
  };
  
  const handleCloseModal = () => setIsOpen(false);

  const handleAddCategory = async (formData) => {
    try {
      // STANDARD plan için kategori limiti kontrolü
      if (userPlan === 'STANDARD' && categories.length >= STANDARD_CATEGORY_LIMIT) {
        toast({
          title: 'Kategori Limiti',
          description: `STANDARD plan için maksimum ${STANDARD_CATEGORY_LIMIT} kategori eklenebilir. Daha fazla kategori eklemek için PREMIUM plana yükseltin.`,
          status: 'warning',
          duration: 5000,
        });
        setIsUpgradeModalOpen(true);
        return;
      }
      
      const response = await menuService.addCategory(formData);
      
      if (response.success && response.data) {
        setCategories(prevCategories => [...prevCategories, response.data]);
        handleCloseModal();
        toast({
          title: 'Başarılı',
          description: 'Kategori başarıyla eklendi',
          status: 'success',
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Kategori ekleme hatası:', error);
      toast({
        title: 'Hata',
        description: 'Kategori eklenirken bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await menuService.deleteCategory(categoryId);
      setCategories(prevCategories => 
        prevCategories.filter(category => category._id !== categoryId)
      );
      toast({
        title: 'Başarılı',
        description: 'Kategori başarıyla silindi',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Kategori silinirken bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Card>
      <Flex direction="column" gap={4}>
        {/* Üst Kısım */}
        <Flex justify="space-between" align="center">
          <Flex align="center" gap={3}>
            <Text fontWeight="bold" fontSize="xl">Kategoriler</Text>
            {userPlan === 'STANDARD' && (
              <Badge colorScheme={categories.length >= STANDARD_CATEGORY_LIMIT ? "red" : "yellow"}>
                {categories.length}/{STANDARD_CATEGORY_LIMIT} Kategori
              </Badge>
            )}
          </Flex>
          <Tooltip 
            label={userPlan === 'STANDARD' && categories.length >= STANDARD_CATEGORY_LIMIT 
              ? `STANDARD plan için maksimum ${STANDARD_CATEGORY_LIMIT} kategori eklenebilir. PREMIUM plana yükseltin.`
              : "Yeni kategori ekle"}
            placement="left"
          >
            <Button
              leftIcon={<AddIcon />}
              colorScheme="red"
              onClick={handleOpenModal}
              size="sm"
              isDisabled={userPlan === 'STANDARD' && categories.length >= STANDARD_CATEGORY_LIMIT}
              opacity={userPlan === 'STANDARD' && categories.length >= STANDARD_CATEGORY_LIMIT ? 0.6 : 1}
            >
              Yeni Kategori
            </Button>
          </Tooltip>
        </Flex>

        {/* Kategori Grid */}
        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
          {categories.map((category) => (
            <Box
              key={category._id}
              p={4}
              bg="white"
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.200"
              position="relative"
              transition="all 0.2s"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'md',
                borderColor: '#e45858',
              }}
            >
              <Flex justify="space-between" align="center">
                <Text 
                  fontWeight="medium" 
                  fontSize="md"
                  color="gray.700"
                >
                  {category.name}
                </Text>
                <IconButton
                  icon={<DeleteIcon />}
                  variant="ghost"
                  colorScheme="red"
                  size="sm"
                  onClick={() => handleDeleteCategory(category._id)}
                  aria-label="Kategoriyi sil"
                  _hover={{
                    bg: 'red.50'
                  }}
                />
              </Flex>
            </Box>
          ))}
        </SimpleGrid>

        {/* Kategori yok ise */}
        {categories.length === 0 && (
          <Flex 
            direction="column" 
            align="center" 
            justify="center" 
            p={8}
            bg="white"
            borderRadius="lg"
            border="1px dashed"
            borderColor="gray.200"
          >
            <Text color="gray.500" mb={4}>Henüz kategori eklenmemiş</Text>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="red"
              variant="outline"
              size="sm"
              onClick={handleOpenModal}
            >
              İlk Kategoriyi Ekle
            </Button>
          </Flex>
        )}
      </Flex>

      <AddCategoryModal
        isOpen={isOpen}
        onClose={handleCloseModal}
        onAdd={handleAddCategory}
      />
      
      <PremiumUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
      />
    </Card>
  );
}